/** Escaped HTML templating. Interpolated values are escaped unless wrapped in raw(). */

export class Raw {
  readonly value: string;
  constructor(value: string) {
    this.value = value;
  }
  toString(): string {
    return this.value;
  }
}

export const raw = (value: string) => new Raw(value);

export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

function render(value: unknown): string {
  if (value instanceof Raw) return value.value;
  if (Array.isArray(value)) return value.map(render).join('');
  if (value === null || value === undefined || value === false) return '';
  return escapeHtml(value);
}

export function html(strings: TemplateStringsArray, ...values: unknown[]): Raw {
  let out = strings[0] ?? '';
  values.forEach((value, i) => {
    out += render(value) + (strings[i + 1] ?? '');
  });
  return new Raw(out);
}

/**
 * Translated text with markup placeholders: the translated template is
 * escaped, `{name}` placeholders take rendered values (Raw stays markup).
 */
export function th(template: string, vars: Record<string, unknown>): Raw {
  let out = '';
  let last = 0;
  for (const match of template.matchAll(/\{(\w+)\}/g)) {
    out += escapeHtml(template.slice(last, match.index));
    out += match[1]! in vars ? render(vars[match[1]!]) : escapeHtml(match[0]);
    last = match.index! + match[0].length;
  }
  return new Raw(out + escapeHtml(template.slice(last)));
}

export function mount(target: Element, content: Raw): void {
  target.innerHTML = content.value;
}

export function qs<T extends Element = HTMLElement>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector as any);
  if (!element) throw new Error(`Missing element ${selector}`);
  return element as T;
}

/** Collect named form controls into a plain object; empty strings become null. */
export function formData(form: HTMLFormElement): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const element of Array.from(form.elements)) {
    const control = element as HTMLInputElement;
    if (!control.name || control.disabled) continue;
    if (control.type === 'radio') {
      if (control.checked) data[control.name] = control.value;
    } else if (control.type === 'checkbox') data[control.name] = control.checked;
    else if (control.type === 'number' || control.type === 'range') data[control.name] = control.value === '' ? null : Number(control.value);
    else data[control.name] = control.value.trim() === '' ? null : control.value.trim();
  }
  return data;
}

/** Drop null/undefined values (for create requests with optional fields). */
export function compact(data: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(data).filter(([, v]) => v !== null && v !== undefined));
}
